import { useServerFn } from "@tanstack/react-start";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { getAnalyticsStats } from "@/lib/analytics.functions";
import { getGhlSubmissions, type GhlSubmission } from "@/lib/ghl.functions";
import { GHL_FORMS } from "@/lib/forms";

export const Route = createFileRoute("/_authenticated/admin/analytics")({
  head: () => ({ meta: [{ title: "Analytics - KCN Admin" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: AnalyticsPage,
});

const RANGES = [7, 30, 90] as const;
type Tab = "stats" | "submissions";

function AnalyticsPage() {
  const [tab, setTab] = useState<Tab>("stats");
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-5">
          <h1 className="headline-md">Analytics</h1>
          <Link to="/admin/seo" className="text-xs uppercase tracking-[0.16em] text-foreground/65 hover:text-primary">SEO Checklist →</Link>
        </div>
        <div className="flex border border-border">
          {(["stats", "submissions"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-2.5 text-xs uppercase tracking-[0.16em] ${
                tab === t ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-foreground"
              }`}
            >
              {t === "stats" ? "Site Stats" : "Form Submissions"}
            </button>
          ))}
        </div>
      </div>
      {tab === "stats" ? <StatsPanel /> : <SubmissionsPanel />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Stats panel                                                        */
/* ------------------------------------------------------------------ */

function StatsPanel() {
  const statsFn = useServerFn(getAnalyticsStats);
  const [range, setRange] = useState<(typeof RANGES)[number]>(30);
  const [data, setData] = useState<Awaited<ReturnType<typeof getAnalyticsStats>> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    statsFn({ data: { rangeDays: range } })
      .then(setData)
      .finally(() => setLoading(false));
  }, [range]);

  const maxDaily = useMemo(() => Math.max(1, ...(data?.daily.map((d) => d.views) ?? [1])), [data]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <span className="text-xs uppercase tracking-[0.16em] text-foreground/65 mr-2">Range</span>
        {RANGES.map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.16em] border ${
              range === r ? "bg-primary text-primary-foreground border-primary" : "border-border text-foreground/70 hover:text-foreground"
            }`}
          >
            {r}d
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-foreground/65">Loading…</p>
      ) : !data ? (
        <p className="text-foreground/65">No data.</p>
      ) : (
        <div className="space-y-8">
          {data.error && (
            <p className="text-primary text-sm">Error reading events: {data.error}</p>
          )}

          {/* Headline metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-border">
            <Metric label="Pageviews" value={fmt(data.pageviews)} />
            <Metric label="Unique visitors" value={fmt(data.uniqueVisitors)} />
            <Metric label="Growth plans created" value={fmt(data.internal.growthPlans)} />
            <Metric label="Audit reports uploaded" value={fmt(data.internal.auditReports)} />
          </div>

          {/* Daily trend */}
          <Section title="Pageview trend" subtitle={`Last ${range} days`}>
            {data.daily.every((d) => d.views === 0) ? (
              <Empty />
            ) : (
              <div className="flex gap-2">
                {/* Y-axis grid labels */}
                <div className="flex flex-col justify-between h-40 text-[11px] tabular-nums text-foreground/55 text-right w-8 pr-1">
                  <span>{maxDaily}</span>
                  <span>{Math.round(maxDaily * 0.75)}</span>
                  <span>{Math.round(maxDaily * 0.5)}</span>
                  <span>{Math.round(maxDaily * 0.25)}</span>
                  <span>0</span>
                </div>
                <div className="flex-1">
                  <div className="relative h-40 border-b border-border border-l">
                    {/* Horizontal grid lines */}
                    {[0, 25, 50, 75, 100].map((pct) => (
                      <div key={pct} className="absolute left-0 right-0 border-t border-border/40" style={{ bottom: `${pct}%` }} />
                    ))}
                    <div className="flex items-end gap-[2px] h-full relative">
                      {data.daily.map((d) => (
                        <div
                          key={d.date}
                          className="group flex-1 h-full flex items-end relative"
                        >
                          <div
                            className="w-full bg-primary/70 group-hover:bg-primary transition-colors"
                            style={{ height: `${Math.round((d.views / maxDaily) * 100)}%`, minHeight: d.views ? "3px" : "0" }}
                          />
                          {/* Hover tooltip */}
                          <div className="pointer-events-none absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-10 hidden group-hover:block whitespace-nowrap bg-black/90 text-cream text-xs px-2 py-1 rounded shadow-lg">
                            {d.date}: {d.views} views
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between text-[11px] uppercase tracking-[0.14em] text-foreground/55 pt-2">
                    <span>{data.daily[0]?.date}</span>
                    <span>Peak {maxDaily}/day</span>
                    <span>{data.daily[data.daily.length - 1]?.date}</span>
                  </div>
                </div>
              </div>
            )}
          </Section>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Top pages */}
            <Section title="Top pages" subtitle="By pageviews">
              {data.topPages.length === 0 ? <Empty /> : (
                <Ranked rows={data.topPages.map((p) => ({ label: p.path, value: p.views }))} />
              )}
            </Section>

            {/* Top referrers */}
            <Section title="Top referrers" subtitle="By visits">
              {data.topReferrers.length === 0 ? <Empty /> : (
                <Ranked rows={data.topReferrers.map((r) => ({ label: r.referrer, value: r.views }))} />
              )}
            </Section>
          </div>

          {/* Funnel */}
          <Section title="Funnel activity" subtitle="CTA clicks, form views, and confirmed submissions by funnel step">
            {data.funnel.every((f) => f.views === 0 && f.clicks === 0 && f.submissions === 0) ? (
              <Empty />
            ) : (
              <div className="border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-surface text-xs uppercase tracking-[0.16em] text-foreground/65">
                    <tr>
                      <th className="text-left p-3">Funnel step</th>
                      <th className="text-right p-3">CTA clicks</th>
                      <th className="text-right p-3">Form views</th>
                      <th className="text-right p-3">Submissions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.funnel.map((f) => (
                      <tr key={f.cta} className="border-t border-border">
                        <td data-label="CTA" className="p-3 capitalize">{f.cta.replace(/_/g, " ")}</td>
                        <td data-label="Clicks" className="p-3 text-right">{fmt(f.clicks)}</td>
                        <td data-label="Views" className="p-3 text-right">{fmt(f.views)}</td>
                        <td data-label="Submissions" className="p-3 text-right whitespace-nowrap">
                          {fmt(f.submissions)}
                          {f.views > 0 ? <span className="text-foreground/55"> · {f.rate}%</span> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Top blog */}
          <Section title="Top blog posts" subtitle="By pageviews">
            {data.topBlog.length === 0 ? <Empty /> : (
              <Ranked rows={data.topBlog.map((p) => ({ label: p.path, value: p.views }))} />
            )}
          </Section>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Submissions panel                                                  */
/* ------------------------------------------------------------------ */

function SubmissionsPanel() {
  const fn = useServerFn(getGhlSubmissions);
  const [formId, setFormId] = useState<string>("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<GhlSubmission[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fn({ data: { formId: formId || undefined, search: search || undefined, limit: 100 } })
      .then((res) => {
        setRows(res.submissions);
        if (res.error) setError(res.error);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [formId, search]);

  function exportCsv() {
    const header = ["Form", "Name", "Email", "Phone", "Submitted", "Raw"];
    const lines = rows.map((r) =>
      [r.formName, r.contactName, r.email, r.phone, r.submittedAt, r.raw]
        .map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`)
        .join(","),
    );
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ghl-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select
          value={formId}
          onChange={(e) => setFormId(e.target.value)}
          className="bg-surface border border-border px-4 py-2.5 text-sm"
        >
          <option value="">All forms</option>
          {GHL_FORMS.map((f) => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
          <option value="visibility_audit">Visibility Audit</option>
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, phone…"
          className="bg-surface border border-border px-4 py-2.5 text-sm flex-1 min-w-[200px]"
        />
        <button
          onClick={exportCsv}
          disabled={rows.length === 0}
          className="border border-border px-5 py-2.5 text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground disabled:opacity-40"
        >
          Export CSV
        </button>
      </div>

      {error && <p className="text-primary text-sm mb-4">{error}</p>}

      {loading ? (
        <p className="text-foreground/65">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-foreground/65">No submissions found.</p>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface text-xs uppercase tracking-[0.16em] text-foreground/65">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Phone</th>
                <th className="text-left p-3">Form</th>
                <th className="text-left p-3">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td data-label="Name" className="p-3 font-medium">{r.contactName || " - "}</td>
                  <td data-label="Email" className="p-3">{r.email || " - "}</td>
                  <td data-label="Phone" className="p-3">{r.phone || " - "}</td>
                  <td data-label="Form" className="p-3">{r.formName}</td>
                  <td data-label="Submitted" className="p-3 text-foreground/75">{r.submittedAt ? new Date(r.submittedAt).toLocaleString() : " - "}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-foreground/50">
        Submissions are pulled live from Agentmoves using a private integration token configured as a backend secret.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Small presentational helpers                                       */
/* ------------------------------------------------------------------ */

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-6 border-r border-b border-border last:border-r-0">
      <p className="text-xs uppercase tracking-[0.16em] text-foreground/65 mb-2">{label}</p>
      <p className="font-display text-3xl">{value}</p>
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-4">
        <h2 className="font-display text-xl">{title}</h2>
        {subtitle && <p className="text-xs uppercase tracking-[0.16em] text-foreground/55 mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Ranked({ rows }: { rows: { label: string; value: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-48 truncate text-sm" title={r.label}>{r.label}</div>
          <div className="flex-1 h-2 bg-surface">
            <div className="h-full bg-primary/60" style={{ width: `${(r.value / max) * 100}%` }} />
          </div>
          <div className="w-10 text-right text-sm tabular-nums">{r.value}</div>
        </div>
      ))}
    </div>
  );
}

function Empty() {
  return <p className="text-foreground/55 text-sm">No data in this range yet.</p>;
}

function fmt(n: number): string {
  return n.toLocaleString("en-US");
}
